import { HashiIsland } from '../interfaces/hashi-island';
import { HashiPuzzleConfig } from '../interfaces/hashi-puzzle-config';

export class HashiPuzzleConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HashiPuzzleConfigError';
  }
}

export interface ParsedHashiPuzzle {
  islands: HashiIsland[];
  puzzleID: string;
}

export function parseHashiPuzzleConfig(raw: HashiPuzzleConfig): ParsedHashiPuzzle {
  if (!raw || typeof raw !== 'object') {
    throw new HashiPuzzleConfigError('Hashi puzzle config is missing or not an object');
  }

  const anyRaw = raw as { puzzle?: unknown; id?: unknown };
  const puzzle = anyRaw.puzzle;

  if (Array.isArray(puzzle) && puzzle.every((row) => Array.isArray(row))) {
    return parseGridForm(puzzle as number[][], typeof anyRaw.id === 'string' ? anyRaw.id : undefined);
  }

  if (puzzle && typeof puzzle === 'object') {
    return parseLegacyForm(puzzle as { game?: { nodes?: unknown }; id?: unknown });
  }

  throw new HashiPuzzleConfigError('Unrecognized hashi puzzle config shape');
}

function parseGridForm(grid: number[][], rawId: string | undefined): ParsedHashiPuzzle {
  const islands: HashiIsland[] = [];
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r];
    if (!Array.isArray(row)) {
      throw new HashiPuzzleConfigError(`Hashi grid row ${r} is not an array`);
    }
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      if (typeof val === 'number' && val > 0) {
        islands.push({ id: `${r},${c}`, x: c, y: r, requiredBridges: val });
      }
    }
  }
  if (islands.length === 0) {
    throw new HashiPuzzleConfigError('Hashi grid contains no islands');
  }
  const puzzleID = rawId ?? `hashi-grid-${hashIslands(islands)}`;
  return { islands, puzzleID };
}

function parseLegacyForm(puzzle: { game?: { nodes?: unknown }; id?: unknown }): ParsedHashiPuzzle {
  const game = puzzle.game;
  if (!game || typeof game !== 'object' || !Array.isArray(game.nodes)) {
    throw new HashiPuzzleConfigError('Hashi legacy config is missing puzzle.game.nodes array');
  }
  const islands = (game.nodes as unknown[]).map((n, i) => coerceIsland(n, i));
  if (islands.length === 0) {
    throw new HashiPuzzleConfigError('Hashi legacy config contains no islands');
  }
  const puzzleID = typeof puzzle.id === 'string' ? puzzle.id : `hashi-legacy-${hashIslands(islands)}`;
  return { islands, puzzleID };
}

// Deterministic 32-bit FNV-1a hash over the canonical island serialization.
// Same set of islands → same puzzleID across reloads and across devices.
function hashIslands(islands: HashiIsland[]): string {
  const canonical = islands
    .map((n) => `${n.x},${n.y},${n.requiredBridges}`)
    .sort()
    .join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

function coerceIsland(raw: unknown, index: number): HashiIsland {
  if (!raw || typeof raw !== 'object') {
    throw new HashiPuzzleConfigError(`Hashi legacy node #${index} is not an object`);
  }
  const node = raw as { id?: unknown; x?: unknown; y?: unknown; requiredBridges?: unknown };
  if (
    typeof node.id !== 'string' ||
    typeof node.x !== 'number' ||
    typeof node.y !== 'number' ||
    typeof node.requiredBridges !== 'number'
  ) {
    throw new HashiPuzzleConfigError(`Hashi legacy node #${index} is missing required fields`);
  }
  return { id: node.id, x: node.x, y: node.y, requiredBridges: node.requiredBridges };
}
